import { useState } from 'react';
import { MonitorPlay, Pencil, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { scrcpyUrl } from '@/lib/scrcpy';
import { endLiveView, startLiveView } from '@/lib/api/liveBoxes';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Box, useBoxes } from '@/lib/api/boxes';
import { auditedDeleteBox, auditedUpdateBox } from '@/lib/api/boxAudit';
import { BoxFormFields, boxFormErrors, boxToForm } from './box-form';

/** Opens scrcpy (ws-scrcpy) for a box and flags it "live" so the agent keeps it
 *  adb-connected and the dashboard greys out its commands. */
export function LiveViewControls({
  ip,
  isLive,
}: {
  ip: string;
  isLive: boolean;
}) {
  const url = scrcpyUrl(ip);
  if (!url) return null;

  const openLive = () => {
    startLiveView(ip).catch((e) => console.error('startLiveView failed', e));
    // The deep link only works once the agent has adb-connected the box and
    // started the on-device scrcpy server (~a few seconds after the lock
    // write). Open the tab now — inside the click gesture, so no popup
    // blocker — but wait before pointing it at the stream. Already-live
    // boxes are ready, so skip the wait.
    const win = window.open('', '_blank');
    if (!win) return;
    win.opener = null;
    win.document.write('Connecting live view… this takes a few seconds.');
    setTimeout(
      () => {
        win.location.href = url;
      },
      isLive ? 0 : 4000
    );
  };

  return (
    <>
      <Button
        variant={isLive ? 'secondary' : 'ghost'}
        size="sm"
        onClick={openLive}
      >
        <MonitorPlay className="mr-1 h-4 w-4" />
        Live view
      </Button>
      {isLive && (
        <Button
          variant="ghost"
          size="sm"
          aria-label="End live view"
          onClick={() => endLiveView(ip)}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </>
  );
}

/** Admin-only per-row Edit button → dialog to change name/type/IP/group/TV or
 *  delete the box. All writes go through the audited helpers (boxAudit). */
export function BoxRowActions({ box }: { box: Box }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(boxToForm(box));
  const [saving, setSaving] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [boxes] = useBoxes();

  const errors = boxFormErrors(form, boxes, box.id);
  const nameChanged = form.name.trim() !== box.name;
  const ipChanged = form.ip.trim() !== box.ip;
  const dirty =
    nameChanged ||
    ipChanged ||
    form.brand !== box.brand ||
    form.group.trim() !== (box.group ?? '') ||
    form.tv.trim() !== (box.tv ?? '');

  const onSave = async () => {
    if (!box.id || errors.name || errors.ip || !dirty) return;
    setSaving(true);
    try {
      const { movedAlert } = await auditedUpdateBox(box, {
        name: form.name,
        ip: form.ip,
        brand: form.brand,
        group: form.group,
        tv: form.tv,
      });
      toast.success(
        ipChanged
          ? `${form.name.trim()} updated — IP ${box.ip} → ${form.ip.trim()}` +
              (movedAlert ? ' (alert monitoring moved)' : '')
          : `${form.name.trim()} updated`
      );
      setOpen(false);
    } catch (error) {
      console.error('update box failed', error);
      toast.error('Failed to update box');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    setSaving(true);
    try {
      await auditedDeleteBox(box);
      toast.success(`${box.name} deleted`);
      setOpen(false);
    } catch (error) {
      console.error('delete box failed', error);
      toast.error('Failed to delete box');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (next) {
            // reset to current on open
            setForm(boxToForm(box));
            setConfirmingDelete(false);
          }
        }}
      >
        <DialogTrigger asChild>
          <Button variant="ghost" size="sm">
            <Pencil className="mr-1 h-4 w-4" />
            Edit
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit box</DialogTitle>
            <DialogDescription>
              {box.name} · {box.ip}
            </DialogDescription>
          </DialogHeader>

          <BoxFormFields
            form={form}
            onChange={setForm}
            errors={errors}
            idPrefix={`edit-${box.id}`}
            nameChanged={nameChanged}
            ipChanged={ipChanged}
          />

          <DialogFooter className="sm:justify-between">
            {confirmingDelete ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-destructive">
                  Delete {box.name}? Alert monitoring for it is removed too.
                </span>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={onDelete}
                  disabled={saving}
                >
                  {saving ? 'Deleting…' : 'Confirm delete'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmingDelete(false)}
                >
                  Keep
                </Button>
              </div>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={() => setConfirmingDelete(true)}
              >
                Delete box…
              </Button>
            )}
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={onSave}
                disabled={saving || !dirty || !!errors.name || !!errors.ip}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
